// Extrahiert Rechnungs-Felder aus dem Text-Layer einer PDF.
//
// Wird als FALLBACK genutzt, wenn der QR-Scan keinen Treffer hatte oder
// der QR-Verwendungszweck nicht wie eine Urkundennummer aussieht.
// Kein OCR — wir nutzen ausschliesslich den Text-Layer der PDF.

export interface TextScanResult {
  /** Bester Kandidat fuer die Urkundennummer (z. B. "K 333/0/1 - 2026"). */
  invoiceNumber: string;
  /** Alle Kandidaten (sortiert nach Score). */
  invoiceNumberCandidates: string[];
  /** Erkannter Gesamtbetrag (EUR), falls eindeutig. */
  total: number | null;
  /** IBAN ohne Leerzeichen, falls vorhanden. */
  iban: string;
}

// Urkundennummern bei DiNo haben das Muster
//   [Prefix-Buchstabe?] <int>/<int>/<int> [-/ ] <2- oder 4-stelliges Jahr>
// Beispiele die wir live gesehen haben:
//   "K 333/0/1 - 2026", "K333/0/1-2026", "UR 12/2024", "K123/4/5 - 26"
// Wir matchen tolerant gegen Leerzeichen und Bindestriche.
const URKUNDE_PATTERNS: RegExp[] = [
  // Variante mit drei Slash-getrennten Zahlen + Jahr
  /\b([A-Z]?\s*\d{1,5}\s*\/\s*\d{1,5}\s*\/\s*\d{1,5}\s*[-–]?\s*\d{2,4})\b/gi,
  // Klassische UR/URNr/Kostenrechnung-Variante mit Schraegstrich + Jahr
  /(?:UR(?:K)?[\.\s\-]*Nr\.?|Kostenrechnung[\-\s]*Nr\.?|Rechnung[\-\s]*Nr\.?)\s*[:\.]?\s*([A-Z]?\s*\d{1,6}\s*\/\s*\d{2,4})/gi,
  // Generischer Fallback: "K <Zahl>/<Jahr>"
  /\b([A-Z]\s*\d{1,6}\s*\/\s*\d{2,4})\b/g,
];

const IBAN_RE = /\b([A-Z]{2}\d{2}(?:\s?[A-Z0-9]){12,30})\b/g;
const AMOUNT_RE =
  /(?:Gesamt(?:summe|betrag)?|Summe|Total|Zu zahlen|Rechnungsbetrag|Endbetrag)[^\d\-]{0,30}([\-]?\s*\d{1,3}(?:[.\s]\d{3})*(?:,\d{2})|\d+,\d{2}|\d+\.\d{2})/gi;

function normalizeCandidate(s: string): string {
  // Whitespace zwischen Buchstabe und Zahl wegnehmen, Bindestriche
  // glaetten — analog zur Backend-Normalisierung
  // (recordNumberDict[invoiceNumber.replace(" ", "").lower()]).
  return s.replace(/\s+/g, ' ').trim();
}

function parseGermanAmount(s: string): number | null {
  // "1.234,56" oder "1234,56" oder "1234.56"
  const cleaned = s.replace(/\s+/g, '');
  if (/^-?\d+,\d{2}$/.test(cleaned)) {
    return Number.parseFloat(cleaned.replace(',', '.'));
  }
  if (/^-?\d{1,3}(?:\.\d{3})+,\d{2}$/.test(cleaned)) {
    return Number.parseFloat(cleaned.replace(/\./g, '').replace(',', '.'));
  }
  if (/^-?\d+\.\d{2}$/.test(cleaned)) {
    return Number.parseFloat(cleaned);
  }
  return null;
}

export function extractInvoiceFieldsFromText(text: string): TextScanResult {
  const candidates: string[] = [];
  for (const re of URKUNDE_PATTERNS) {
    const flagged = re.flags.includes('g')
      ? re
      : new RegExp(re.source, re.flags + 'g');
    const matches = Array.from(text.matchAll(flagged));
    for (const m of matches) {
      const raw = (m[1] ?? m[0] ?? '').trim();
      if (raw.length >= 3 && !candidates.includes(raw)) {
        candidates.push(normalizeCandidate(raw));
      }
    }
  }

  // IBAN
  let iban = '';
  const ibanMatch = text.match(IBAN_RE);
  if (ibanMatch && ibanMatch[0]) {
    iban = ibanMatch[0].replace(/\s+/g, '').toUpperCase();
  }

  // Betrag — wir nehmen den ersten plausiblen Treffer aus dem typischen
  // Summen-Kontext. Free-form Beträge ohne Label ignorieren wir, weil sie
  // zu oft danebenliegen.
  let total: number | null = null;
  const amountMatches = Array.from(text.matchAll(AMOUNT_RE));
  for (const m of amountMatches) {
    const n = parseGermanAmount(m[1] ?? '');
    if (n !== null) {
      total = n;
      break;
    }
  }

  return {
    invoiceNumber: candidates[0] ?? '',
    invoiceNumberCandidates: candidates,
    total,
    iban,
  };
}
